import Tile from "@shared/components/ui-kit/form/Tile/Tile.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import Text from "@shared/components/ui-kit/typography/Text/Text.web";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { useTranslation } from "@shared/utils/internationalization";
import { useCallback, useEffect, useState } from "react";

import { ASSIGNED_SEATING } from "@shared/constants";
import useAssignedSeatingHelpers from "@shared/hooks/seating/useAssignedSeatingHelpers/useAssignedSeatingHelpers";
import useDebounce from "@shared/hooks/useDebounce";
import useWindowSize from "@shared/hooks/useWindowSize";
import { SeatWithLocationDataType } from "@shared/modules/seating/types/seating-types";
import Track from "@shared/modules/analytics/components/Track/Track.web";
import breakpoints from "@shared/theme/props/breakpoints";
import AssignedSeatInformation from "../../../../components/AssignedSeatInformation/AssignedSeatInformation.web";
import { BestAvailableSeatingProps } from "../../../../components/SidebarSeatingContainer/SidebarSeatingContainer.props";

export default function BestAvailableSeatingItem({
  item,
  isSelected,
  onSelect,
}: {
  item: BestAvailableSeatingProps["bestAvailableSeating"][0];
  isSelected: boolean;
  onSelect: () => void;
}) {
  const {
    stageServiceState,
    synchronizeSeating,
    isLoadingMap,
    bestAvailableSeatingQueryParams,
  } = useSeatingState();
  const { handleSeatSelect } = useAssignedSeatingHelpers();
  const { t } = useTranslation("seating");
  const [isHovering, setIsHovering] = useState(false);
  const { width } = useWindowSize();
  const isMobile = width && width < breakpoints.sm;

  // Only take the debounced value, ignore loading state
  const debouncedIsHovering = useDebounce(isHovering, 150)[0];

  const infoType = item.gaInfo ?? item.seatingInfo;

  const handleItemClick = () => {
    if (item.gaInfo) {
      stageServiceState.removeAllBestAvailableGaSelections();

      const obj = {
        permissionIds: item.gaInfo?.permissionsMap?.[item.id]?.filter(
          (id): id is number => id !== undefined
        ),
        itemTypeIds: [
          ...(item.gaInfo?.ticketTypesMap?.[item.id] ?? []),
          ...(item.gaInfo?.membershipLevelsMap?.[item.id] ?? []),
        ],
        qtyMap: bestAvailableSeatingQueryParams.ticket_types,
        name: ASSIGNED_SEATING.GENERAL_ADMISSION.ID,
        identifier: item.gaInfo?.identifier ?? "",
        segment_identifier: item.gaInfo?.segment_identifier ?? "",
        membershipIds: item.gaInfo?.membershipLevelsMap?.[item.id],
        price: item.gaInfo?.pricingMap?.[item.id],
        qty: bestAvailableSeatingQueryParams.quantity,
        id: item.id,
      };
      stageServiceState.handleGaVisualization({
        formattedSelections: obj,
      });
    } else if (item.seatingInfo) {
      const identifiers = item.seatingInfo.identifier
        .split(",")
        .map((id) => id.trim());

      const seatData = {
        seatIdentifiers: identifiers,
        seatIds: item.seatingInfo.seatIds,
        segmentIdentifier: item.seatingInfo.segment_identifier,
        locationIdentifier: item.seatingInfo.location_identifier,
        price: item.price,
        permissionsMap: item.seatingInfo.permissionsMap,
        pricingMap: item.seatingInfo.pricingMap,
        ticketTypesMap: item.seatingInfo.ticketTypesMap,
        membershipLevelsMap: item.seatingInfo.membershipLevelsMap,
      };

      // used to get the location data for the seat
      const seatDataAndPermissions =
        stageServiceState.getSeatDataAndPermissions(seatData.seatIds[0]);

      let locationData;
      if (seatDataAndPermissions && seatDataAndPermissions.$data) {
        locationData = seatDataAndPermissions.$data.locationData;
      }

      stageServiceState.handleSeatingVisualization({
        seats: { ...seatData, locationData }, // locationData is needed for removing full purchase tables
        selectedIndividually: false,
        onSelect: () => onSelect(),
        isMobile: Boolean(isMobile),
        shouldZoom: true, // Best available selections should zoom and center
        cb: (seat?: SeatWithLocationDataType) => {
          if (seat) {
            handleSeatSelect({ $data: seat });
          }
          synchronizeSeating();
        },
      });
    }
    synchronizeSeating();
  };

  // Create the highlight function
  const highlightSegment = useCallback(() => {
    if (!infoType) return;
    const identifiers = infoType.identifier.split(",").map((id) => id.trim());

    const seatData = {
      id: item.id,
      seatIdentifiers: identifiers,
      segmentIdentifier: infoType.segment_identifier,
      locationIdentifier: infoType.location_identifier,
    };

    stageServiceState.highlightSegment(seatData, true);
  }, [item, stageServiceState]);

  const removeHighlights = useCallback(() => {
    // Only call these methods if stageServiceState is ready
    if (stageServiceState && stageServiceState.layers?.mainLayer) {
      stageServiceState.destroyExistingHoverHighlights();
      stageServiceState.removeAllSegmentHighlights();
    }
  }, [stageServiceState]);

  // Apply highlight effects based on debounced hover state
  useEffect(() => {
    if (isMobile || isLoadingMap) {
      return undefined;
    }

    if (debouncedIsHovering) {
      highlightSegment();
    } else {
      removeHighlights();
    }

    // Cleanup function to ensure highlights are removed when component unmounts
    return () => {
      removeHighlights();
    };
  }, [
    debouncedIsHovering,
    highlightSegment,
    removeHighlights,
    isMobile,
    isLoadingMap,
  ]);

  return (
    <Track.Click
      analytics={{
        label: "best-available-suggestion-click",
        custom_params: {
          // to determine if the permissions in the cart are from a suggestion or not
          permission_ids: Object.values(infoType?.permissionsMap ?? {}).flat(),
        },
      }}
    >
      <Tile.Complex
        id={infoType?.identifier ?? "best-available-seating-item"}
        width="100%"
        disabled={isLoadingMap}
        onClick={handleItemClick}
        isSelected={isSelected}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Header with title */}
        <Text fontSize="md" fontWeight="bold">
          {item.label}
        </Text>

        {/* Price and rank badge row */}
        <Flex justify="space-between" align="center">
          <Text fontSize="sm">
            {t("from {{price}} /ea", { price: item.price })}
          </Text>
        </Flex>

        {/* Seating information */}
        <Box bg="bg.subtle" p="2xs" borderRadius="sm" width="100%">
          <AssignedSeatInformation
            identifier={infoType?.identifier}
            segment_identifier={infoType?.segment_identifier}
            location_identifier={infoType?.location_identifier}
            isAccessible={
              infoType && "is_accessible" in infoType // only seats have is_accessible
                ? infoType.is_accessible
                : false
            }
          />
        </Box>
      </Tile.Complex>
    </Track.Click>
  );
}
